// src/components/pages/integrations/drchrono/DrChronoIntegrationCard.tsx

import React, { useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Alert } from "react-native";
import { TIntegration } from "../../../../../services/api-integrations";
import {
  initiateDrChronoOAuthTopAesthetics,
  initiateDrChronoOAuthCameoSurgery,
  requestIntegrationAccess,
} from "../../../../../services/api-integrations-drchrono";
import DrChronoTemplateExplorer from "./DrchronoTemplateDebug";
import { TSession } from "../../../../../utils/types";

interface DrChronoIntegrationCardProps {
  integrations: TIntegration[];
  showTopAesthetics: boolean;
  showCameoSurgery: boolean;
  session: TSession;
}

const DrChronoIntegrationCard: React.FC<DrChronoIntegrationCardProps> = ({
  integrations,
  showTopAesthetics,
  showCameoSurgery,
  session,
}) => {
  const [isLoading, setIsLoading] = useState({
    topAesthetics: false,
    cameoSurgery: false,
    request: false,
  });
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [showTemplateExplorer, setShowTemplateExplorer] = useState(false);

  // Check if Dr. Chrono is connected
  const isDrChronoConnected = integrations.some(
    (integration) =>
      integration.integration_type === "drchrono" && integration.is_active,
  );

  const handleConnectDrChronoTopAesthetics = async () => {
    setIsLoading((prev) => ({ ...prev, topAesthetics: true }));
    try {
      await initiateDrChronoOAuthTopAesthetics();
    } catch (error: any) {
      Alert.alert("Error", error.message || "Failed to connect to Dr. Chrono");
    } finally {
      setIsLoading((prev) => ({ ...prev, topAesthetics: false }));
    }
  };

  const handleConnectDrChronoCameoSurgery = async () => {
    setIsLoading((prev) => ({ ...prev, cameoSurgery: true }));
    try {
      await initiateDrChronoOAuthCameoSurgery();
    } catch (error: any) {
      Alert.alert("Error", error.message || "Failed to connect to Dr. Chrono");
    } finally {
      setIsLoading((prev) => ({ ...prev, cameoSurgery: false }));
    }
  };

  const handleExploreTemplates = () => {
    setShowTemplateExplorer(!showTemplateExplorer);
  };

  const handleRequestDrChronoIntegration = async () => {
    setIsLoading((prev) => ({ ...prev, request: true }));
    try {
      const response = await requestIntegrationAccess(session, {
        integration_type: "drchrono",
        source: "integrations_page_drchrono_card",
      });
      setRequestSubmitted(true);
      Alert.alert("Request Sent", response.message);
    } catch (error: any) {
      Alert.alert(
        "Error",
        error?.message || "Failed to submit DrChrono integration request.",
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, request: false }));
    }
  };

  const renderConnectionButtons = () => (
    <View style={styles.buttonContainer}>
      {showTopAesthetics && (
        <TouchableOpacity
          style={[
            styles.connectButton,
            isLoading.topAesthetics && styles.disabledButton,
          ]}
          onPress={handleConnectDrChronoTopAesthetics}
          disabled={isLoading.topAesthetics}
        >
          <Text style={styles.buttonText}>
            {isLoading.topAesthetics
              ? "Connecting..."
              : "Connect Top Aesthetics"}
          </Text>
        </TouchableOpacity>
      )}

      {showCameoSurgery && (
        <TouchableOpacity
          style={[
            styles.connectButton,
            styles.cameoButton,
            isLoading.cameoSurgery && styles.disabledButton,
          ]}
          onPress={handleConnectDrChronoCameoSurgery}
          disabled={isLoading.cameoSurgery}
        >
          <Text style={styles.buttonText}>
            {isLoading.cameoSurgery ? "Connecting..." : "Connect Cameo Surgery"}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderConnectedView = () => (
    <View style={styles.connectedContainer}>
      <View style={styles.connectedStatus}>
        <Text style={styles.connectedText}>✅ Connected</Text>
      </View>

      <TouchableOpacity
        style={styles.exploreButton}
        onPress={handleExploreTemplates}
      >
        <Text style={styles.buttonText}>
          {showTemplateExplorer ? "📋 Hide Templates" : "📋 Explore Templates"}
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.quickConnectSection}>
      <Text style={styles.sectionTitle}>Quick Connect</Text>

      <View style={styles.integrationCard}>
        <Text style={styles.integrationTitle}>Dr. Chrono</Text>
        <Text style={styles.integrationDescription}>
          Sync patients and appointments from Dr. Chrono
        </Text>

        {(showTopAesthetics || showCameoSurgery) &&
          (isDrChronoConnected
            ? renderConnectedView()
            : renderConnectionButtons())}

        <View style={styles.requestSection}>
          <Text style={styles.requestDescription}>
            Need DrChrono enabled for your clinic?
          </Text>
          <TouchableOpacity
            style={[
              styles.requestButton,
              isLoading.request && styles.disabledButton,
            ]}
            onPress={handleRequestDrChronoIntegration}
            disabled={isLoading.request}
          >
            <Text style={styles.buttonText}>
              {isLoading.request
                ? "Submitting..."
                : "Request Free DrChrono Integration"}
            </Text>
          </TouchableOpacity>
          {requestSubmitted && (
            <Text style={styles.requestSuccessText}>
              Request received. Our team will follow up shortly.
            </Text>
          )}
        </View>
      </View>

      {/* Template Explorer - Show when toggled */}
      {isDrChronoConnected && showTemplateExplorer && (
        <DrChronoTemplateExplorer session={session} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  quickConnectSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#495057",
    marginBottom: 16,
  },
  integrationCard: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  integrationTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
    color: "#212529",
  },
  integrationDescription: {
    fontSize: 14,
    color: "#6c757d",
    marginBottom: 16,
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  connectButton: {
    backgroundColor: "#007bff",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    minWidth: 140,
  },
  cameoButton: {
    backgroundColor: "#6f42c1",
  },
  disabledButton: {
    backgroundColor: "#6c757d",
  },
  buttonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
  connectedContainer: {
    gap: 12,
  },
  connectedStatus: {
    flexDirection: "row",
    alignItems: "center",
  },
  connectedText: {
    color: "#28a745",
    fontSize: 16,
    fontWeight: "600",
  },
  requestSection: {
    marginTop: 16,
    gap: 10,
  },
  requestDescription: {
    fontSize: 13,
    color: "#6c757d",
  },
  requestButton: {
    backgroundColor: "#34C759",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    alignSelf: "flex-start",
  },
  requestSuccessText: {
    fontSize: 13,
    color: "#28a745",
    fontWeight: "600",
  },
  exploreButton: {
    backgroundColor: "#34C759",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    alignSelf: "flex-start",
  },
});

export default DrChronoIntegrationCard;
